import { inject, Injectable } from '@angular/core';
import { GlobalService } from '../../../shared/services/global-data/global.service';
import { ClientState } from '../states/client.service';
import { FarmState } from '../states/farm.service';

type ScopedListingState<T> = {
  clientId: number;
  farmId: number;
  value: T;
};

@Injectable({
  providedIn: 'root'
})
export class PageMemoryStateService {
  private readonly farmState = inject(FarmState);
  private readonly clientState = inject(ClientState);
  private readonly globalService = inject(GlobalService);
  private readonly state = new Map<string, unknown>();

  private set<T>(key: string, value: T): void {
    this.state.set(key, this.clone(value));
  }

  private get<T>(key: string): T | null {
    if (!this.state.has(key)) {
      return null;
    }

    return this.clone(this.state.get(key) as T);
  }

  private remove(key: string): void {
    this.state.delete(key);
  }

  private clone<T>(value: T): T {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value)) as T;
  }

  restorePageState<T>(key: string): T | null {
    const clientId = this.clientState.get()?.clientId;
    const farmId = this.farmState.get()?.farmId;
    const state = this.get<ScopedListingState<T>>(key);

    if (!state || !clientId || !farmId) {
      this.remove(key);
      return null;
    }

    if (state.clientId !== clientId || state.farmId !== farmId) {
      this.remove(key);
      return null;
    }

    return state.value;
  }

  rememberPageState<T>(key: string, value: T): void {
    if (!this.globalService.isMobileStyle) {
      return;
    }

    const clientId = this.clientState.get()?.clientId;
    const farmId = this.farmState.get()?.farmId;

    if (!clientId || !farmId) {
      this.remove(key);
      return;
    }

    this.set<ScopedListingState<T>>(key, {
      clientId,
      farmId,
      value
    });
  }

  clearPageState(key: string): void {
    this.remove(key);
  }

  clearAllPageStates(): void {
    this.state.clear();
  }
}
